import db from '../db/index';
import moment from 'moment';
import { collection, getDocs, getDoc, doc, updateDoc, query, where, onSnapshot, deleteDoc } from 'firebase/firestore';
const time = moment();
const now = time.format('LLLL');

const getCheckInData = async uid => {
    const checkIns = {};
    const checkInsSnapshot = await getDocs(collection(db, 'profiles', uid, 'checkIns'));

    checkInsSnapshot.docs
        .reverse() // order the documents by date in descending order
        .slice(0, 7) // get the 7 most recent survey
        .forEach(doc => {
            checkIns[doc.id] = doc.data();
        });

    let selectedDate = null;
    if (checkInsSnapshot.docs.length > 0) {
        selectedDate = checkInsSnapshot.docs[checkInsSnapshot.docs.length - 1].id;
    }

    return {
        checkIns,
        selectedDate,
    };
};

const getCheckInDataByDate = async(uid, selectedDate) => {
    const date = moment(selectedDate);

    const docRef = doc(db, 'profiles', uid, 'checkIns', date.format('YYYY-MM'));
    const docSnapshot = await getDoc(docRef);

    if (docSnapshot.exists()) {
        return docSnapshot.data()[date.format('YYYY-MM-DD')];
    }

    return null;
};

const getCheckInDates = async uid => {
    const checkInsSnapshot = await getDocs(collection(db, 'profiles', uid, 'checkIns'));
    let dates = [];

    checkInsSnapshot.docs.forEach(doc => {
        dates = dates.concat(Object.keys(doc.data()));
    });

    return dates;
};

const getAllUsers = async() => {
    const users = {};
    const usersSnapshot = await getDocs(collection(db, 'profiles'));
    usersSnapshot.docs.forEach(doc => {
        users[doc.id] = doc.data();
    });
    return users;
};

const getCheckInStatus = async uid => {
    const docSnap = await getDoc(doc(db, 'profiles', uid));
    return docSnap.data().checkin;
};

const getEmailPreference = async uid => {
    const docSnap = await getDoc(doc(db, 'profiles', uid));
    return docSnap.data().receiveEmail;
};

const getAvatarName = async uid => {
    const docSnap = await getDoc(doc(db, 'profiles', uid));
    return docSnap.data().avatarName;
};

const getPropsAvatar = async uid => {
    const docSnap = await getDoc(doc(db, 'profiles', uid));
    return docSnap.data().avatar;
};

const updatePropsAvatar = async({ colorProps, styleProps, uid }) => {
    await updateDoc(doc(db, 'profiles', uid), {
        avatar: { styles: styleProps, colors: colorProps },
    });
    await updateDoc(doc(db, 'profiles', uid), {
        created_avatar: true,
    });
};

const getAvatarCreationStatus = async uid => {
    const userSnapshot = await getDoc(doc(db, 'profiles', uid));
    const user = userSnapshot.data();
    return user.created_avatar;
};

const getFullName = async uid => {
    const docSnap = await getDoc(doc(db, 'profiles', uid));
    return docSnap.data().fullName;
};

const getAvatarMetaData = async uid => {
    const userDoc = await getDoc(doc(db, 'profiles', uid));
    const avatar = userDoc.data().avatar;

    return {
        avatarColors: avatar.colors,
        avatarStyles: avatar.styles,
    };
};

const getEmailAddress = async uid => {
    const docSnap = await getDoc(doc(db, 'profiles', uid));
    return docSnap.data().email;
};

const updateEmailPreference = async({ isToggled, uid }) => {
    await updateDoc(doc(db, 'profiles', uid), {
        receiveEmail: !isToggled,
    });
};

const updateEmailReminders = async({ reminder, uid }) => {
    await updateDoc(doc(db, 'profiles', uid), {
        emailReminders: reminder,
    });
};

const getEmailReminders = async uid => {
    const docSnap = await getDoc(doc(db, 'profiles', uid));
    if (docSnap.data().emailReminders === undefined || docSnap.data().emailReminders.length === 0) {
        return null;
    } else {
        return docSnap.data().emailReminders;
    }
};

const getEmailReminderTime = async uid => {
    const docSnap = await getDoc(doc(db, 'profiles', uid));
    if (docSnap.data().emailReminderTime === undefined || docSnap.data().emailReminderTime.length === 0) {
        return null;
    } else {
        // return { daysLeft: '', hoursLeft: '', minutesLeft: '' };
        return docSnap.data().emailReminderTime;
    }
};

const updateEmailReminderTime = async({ reminderTime, uid }) => {
    updateDoc(doc(db, 'profiles', uid), {
        emailReminderTime: reminderTime,
    });
};

const getDisplayPreference = async uid => {
    const docSnap = await getDoc(doc(db, 'profiles', uid));
    return docSnap.data().allowDisplay;
};

const getAllAvatarNames = async() => {
    const avatarNames = [];
    const usersSnapshot = await getDocs(collection(db, 'profiles'));
    usersSnapshot.docs.forEach(doc => {
        avatarNames.push(doc.data().avatarName);
    });
    return avatarNames;
};

const updateAvatarName = async(name, uid) => {
    await updateDoc(doc(db, 'profiles', uid), {
        avatarName: name,
    });
};

const updateDisplayPreference = async({ isToggled, uid }) => {
    await updateDoc(doc(db, 'profiles', uid), {
        allowDisplay: isToggled,
    });
};

const getLeaderboardData = (setLeaderboardData) => {
    const q = query(collection(db, 'profiles'), where('allowDisplay', '==', true));

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
        const usersArray = querySnapshot.docs
            .map(doc => ({ id: doc.id, ...doc.data() }))
            .sort((a, b) => b.streaks - a.streaks)
            .slice(0, 9);

        setLeaderboardData(usersArray);
    });

    return unsubscribe;
};

const deleteUserSubcollections = async(uid, subcollectionName) => {
    const subcollectionRef = await getDocs(collection(db, 'profiles', uid, subcollectionName));
    const deletions = subcollectionRef.docs.map((doc) => deleteDoc(doc.ref));
    await Promise.all(deletions);
};

const deleteUserFirestoreData = async(uid) => {
    // Delete documents in the 'checkIns' subcollection
    await deleteUserSubcollections(uid, 'checkIns');

    // After all subcollections are deleted, delete the profile document itself
    const userProfileRef = doc(db, 'profiles', uid);
    await deleteDoc(userProfileRef);
};

export {
    getCheckInData,
    getCheckInDataByDate,
    getCheckInDates,
    getAvatarMetaData,
    getEmailPreference,
    getAvatarName,
    getPropsAvatar,
    getFullName,
    getAvatarCreationStatus,
    updateEmailPreference,
    updatePropsAvatar,
    getAllUsers,
    updateEmailReminders,
    updateEmailReminderTime,
    getEmailReminders,
    getEmailReminderTime,
    getDisplayPreference,
    updateDisplayPreference,
    getAllAvatarNames,
    updateAvatarName,
    getLeaderboardData,
    deleteUserFirestoreData,
    getEmailAddress,
    getCheckInStatus,
    now,
};
